import { Text, WrapText } from 'lucide-react';
import { useState } from 'react';
import { Dropdown, Header, Icon, Input, Popup } from 'semantic-ui-react';
import ToggleGroup from '../../toggleGroup';
import { ConfigProps, KeyValueOptions } from './constants';
import { CheckboxBtn } from './fields';
import './styles.css';
import BorderVertical from '../../icons/borderVertical';
import BorderHorizontal from '../../icons/borderHorizontal';

function NewClientsAdvancedSettings({ tableConfig, onTableConfigChange, chartsRef }) {
    const onTableConfigPropChange = (prop, val) => {
        onTableConfigChange(Object.assign({}, tableConfig, { [prop]: val }));
    };

    return (
        <div className="table-config-segment advanced">
            <HeaderColumFormat config={tableConfig} onPropChange={onTableConfigPropChange} />
            <TableFormat config={tableConfig} onPropChange={onTableConfigPropChange} />
            <FieldsSettings config={tableConfig} onPropChange={onTableConfigPropChange} />
            <Calculations config={tableConfig} onPropChange={onTableConfigPropChange} />
            <Others config={tableConfig} onPropChange={onTableConfigPropChange} chartsRef={chartsRef} />
        </div>
    );
}

function ConfigSection({ children }) {
    return <div className="config-section">{children}</div>;
}

function ConfigSectionHeader({ children }) {
    return (
        <Header className="config-section__header" as="h4" textAlign="center">
            {children}
        </Header>
    );
}

function ConfigSectionBody({ children, className = '' }) {
    return <div className={`config-section__body ${className}`}>{children}</div>;
}

function HeaderColumFormat({ config, onPropChange }) {
    return (
        <ConfigSection>
            <ConfigSectionHeader>Header Column Format</ConfigSectionHeader>
            <ConfigSectionBody className="header-column-format">
                <div className="config-module">
                    <ModuleHeader text="Font Size" />
                    <StepInput
                        value={
                            config[ConfigProps.HEADER_COLUMN_FONT_SIZE] === 'Default'
                                ? 14
                                : config[ConfigProps.HEADER_COLUMN_FONT_SIZE]
                        }
                        onChange={(value) =>
                            onPropChange(ConfigProps.HEADER_COLUMN_FONT_SIZE, value == 14 ? 'Default' : value)
                        }
                        step={1}
                        min={10}
                        max={14}
                    />
                </div>
                <div className="config-module">
                    <ModuleHeader text="Wrap Text" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.EQUAL_ROWS_HIGH]}
                        onChange={(value) => onPropChange(ConfigProps.EQUAL_ROWS_HIGH, value)}
                    >
                        <ToggleGroup.Button value={true}>
                            <Popup trigger={<Text strokeWidth={1} size={20} />} content="Single line" />
                        </ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>
                            <Popup trigger={<WrapText strokeWidth={1} size={20} />} content="Wrapped lines" />
                        </ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Scale" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SCALE_COLUMN]}
                        onChange={(value) => onPropChange(ConfigProps.SCALE_COLUMN, value)}
                    >
                        <ToggleGroup.Button value={true}>Column</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>In-Text</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Width" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.HEADER_COLUMN_WIDTH]}
                        onChange={(value) => onPropChange(ConfigProps.HEADER_COLUMN_WIDTH, value)}
                    >
                        {KeyValueOptions.HEADER_COLUMN_WIDTH_OPS.map((option) => (
                            <ToggleGroup.Button key={option.key} value={option.key}>
                                {option.key}
                            </ToggleGroup.Button>
                        ))}
                    </ToggleGroup>
                </div>
            </ConfigSectionBody>
        </ConfigSection>
    );
}

function TableFormat({ config, onPropChange }) {
    return (
        <ConfigSection>
            <ConfigSectionHeader>Table Format</ConfigSectionHeader>
            <ConfigSectionBody>
                <PeriodsStyleSettings config={config} onPropChange={onPropChange} />
                <LinesSettings config={config} onPropChange={onPropChange} />
                <div className="config-module">
                    <ModuleHeader text="Decimal Precision" />
                    <StepInput
                        max={6}
                        min={0}
                        onChange={(value) => onPropChange(ConfigProps.FLOATING_POINT_PRECISION, value)}
                        step={1}
                        value={config[ConfigProps.FLOATING_POINT_PRECISION]}
                    />
                </div>
                <div className="config-module">
                    <ModuleHeader text="Thousand Separator" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.NUMBER_LOCALE]}
                        onChange={(value) => onPropChange(ConfigProps.NUMBER_LOCALE, value)}
                    >
                        {KeyValueOptions.THOUSANDS_SEPARATOR_OPS.map((option) => (
                            <ToggleGroup.Button key={option.key} value={option.key}>
                                {option.value}
                            </ToggleGroup.Button>
                        )).reverse()}
                    </ToggleGroup>
                </div>
            </ConfigSectionBody>
        </ConfigSection>
    );
}

function FieldsSettings({ config, onPropChange }) {
    return (
        <ConfigSection>
            <ConfigSectionHeader>Field Settings</ConfigSectionHeader>
            <ConfigSectionBody className="field-settings">
                <div className="config-module">
                    <ModuleHeader text="Empty Rows" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.HIDE_EMPTY_ROWS]}
                        onChange={(value) => onPropChange(ConfigProps.HIDE_EMPTY_ROWS, value)}
                    >
                        <ToggleGroup.Button value={false}>Show</ToggleGroup.Button>
                        <ToggleGroup.Button value={true}>Hide</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="View Fields" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SHOW_IMPORTANT_ROWS_ONLY]}
                        onChange={(value) => onPropChange(ConfigProps.SHOW_IMPORTANT_ROWS_ONLY, value)}
                    >
                        <ToggleGroup.Button value={false}>All</ToggleGroup.Button>
                        <ToggleGroup.Button value={true}>Key Only</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Category Mode" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.CATEGORY_BASED_MODE]}
                        onChange={(value) => onPropChange(ConfigProps.CATEGORY_BASED_MODE, value)}
                    >
                        <ToggleGroup.Button value={true}>On</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Off</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Labels" />
                    <ToggleGroup
                        activeValue={String(config[ConfigProps.LABELS_FORMAT])}
                        onChange={(value) => onPropChange(ConfigProps.LABELS_FORMAT, value)}
                    >
                        {KeyValueOptions.LABELS_FORMAT_OPS.map((option) => (
                            <ToggleGroup.Button key={option.key} value={option.key}>
                                <Popup
                                    trigger={<span className="settings-ellipsis">{option.value}</span>}
                                    content={option.key}
                                />
                            </ToggleGroup.Button>
                        ))}
                    </ToggleGroup>
                </div>
            </ConfigSectionBody>
        </ConfigSection>
    );
}

function Calculations({ config, onPropChange }) {
    return (
        <ConfigSection>
            <ConfigSectionHeader>Calculations</ConfigSectionHeader>
            <ConfigSectionBody>
                <div className="config-module">
                    <ModuleHeader text="Margin of Error %" />
                    <StepInput
                        max={10}
                        min={0}
                        onChange={(value) => onPropChange(ConfigProps.DIFF_DETECTION_THRESHOLD, value)}
                        step={1}
                        value={config[ConfigProps.DIFF_DETECTION_THRESHOLD]}
                    />
                </div>
                <div className="config-module">
                    <ModuleHeader text="Consider “-” & “0” as:" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.DIFF_DETECTION_IGNORE_ZERO_DASH]}
                        onChange={(value) => onPropChange(ConfigProps.DIFF_DETECTION_IGNORE_ZERO_DASH, value)}
                    >
                        <ToggleGroup.Button value={true}>Same</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Different</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Missing Periods" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SHOW_CALCULATED_STATEMENTS]}
                        onChange={(value) => onPropChange(ConfigProps.SHOW_CALCULATED_STATEMENTS, value)}
                    >
                        <ToggleGroup.Button value={true}>Calculate</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Off</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="3D Ratios" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SHOW_3D_RATIOS]}
                        onChange={(value) => onPropChange(ConfigProps.SHOW_3D_RATIOS, value)}
                    >
                        <ToggleGroup.Button value={true}>Calculate</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Off</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
            </ConfigSectionBody>
        </ConfigSection>
    );
}

function Others({ config, onPropChange, chartsRef }) {
    return (
        <ConfigSection>
            <ConfigSectionHeader>Others</ConfigSectionHeader>
            <ConfigSectionBody>
                <div className="config-module">
                    <ModuleHeader text="Charts" />
                    <ToggleGroup
                        activeValue={!!config[ConfigProps.SHOW_CHARTS]}
                        onChange={(value) => {
                            if (value && chartsRef.current) {
                                chartsRef.current.scrollIntoView({ behavior: 'smooth' });
                            }
                            onPropChange(ConfigProps.SHOW_CHARTS, value);
                        }}
                    >
                        <ToggleGroup.Button value={true}>Show</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Hide</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="High Growth % Warning" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SHOW_HIGH_GROWTH_PERCENTAGES_WARNING]}
                        onChange={(value) => onPropChange(ConfigProps.SHOW_HIGH_GROWTH_PERCENTAGES_WARNING, value)}
                    >
                        <ToggleGroup.Button value={true}>Show</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Hide</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Info Icons" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SHOW_INFO_ICONS]}
                        onChange={(value) => onPropChange(ConfigProps.SHOW_INFO_ICONS, value)}
                    >
                        <ToggleGroup.Button value={true}>Show</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Hide</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
                <div className="config-module">
                    <ModuleHeader text="Missing Data in Latest" />
                    <ToggleGroup
                        activeValue={config[ConfigProps.SHOW_ORIGINAL_CELL_DATA_IF_LATEST_MISSING]}
                        onChange={(value) => onPropChange(ConfigProps.SHOW_ORIGINAL_CELL_DATA_IF_LATEST_MISSING, value)}
                    >
                        <ToggleGroup.Button value={true}>Show Original</ToggleGroup.Button>
                        <ToggleGroup.Button value={false}>Empty</ToggleGroup.Button>
                    </ToggleGroup>
                </div>
            </ConfigSectionBody>
        </ConfigSection>
    );
}

function PeriodsStyleSettings({ config, onPropChange }) {
    const periodOptions = KeyValueOptions.PERIOD_CLASS_OPS.map((kv) => ({
        key: kv.key,
        text: kv.value,
        value: kv.key,
    }));
    const onPeriodClassChange = (prop, val) => {
        onPropChange(
            ConfigProps.PERIOD_CLASSES,
            Object.assign({}, config[ConfigProps.PERIOD_CLASSES], { [prop]: val }),
        );
    };
    return (
        <div className="config-module">
            <ModuleHeader text="Column Colors" />
            <div>
                <Dropdown
                    value={config[ConfigProps.PERIOD_CLASSES][ConfigProps.FY_CLASS]}
                    trigger={
                        <div
                            className={`checkboxBtn checked period-class-btn ${
                                config[ConfigProps.PERIOD_CLASSES][ConfigProps.FY_CLASS]
                            }`}
                        >
                            FY
                        </div>
                    }
                    icon={null}
                    options={periodOptions}
                    onChange={(_, data) => {
                        onPeriodClassChange(ConfigProps.FY_CLASS, data.value);
                    }}
                />
                <Dropdown
                    value={config[ConfigProps.PERIOD_CLASSES][ConfigProps.NINE_M_CLASS]}
                    trigger={
                        <div
                            className={`checkboxBtn checked period-class-btn ${
                                config[ConfigProps.PERIOD_CLASSES][ConfigProps.NINE_M_CLASS]
                            }`}
                        >
                            9M
                        </div>
                    }
                    icon={null}
                    options={periodOptions}
                    onChange={(_, data) => {
                        onPeriodClassChange(ConfigProps.NINE_M_CLASS, data.value);
                    }}
                />
                <Dropdown
                    value={config[ConfigProps.PERIOD_CLASSES][ConfigProps.HS_CLASS]}
                    trigger={
                        <div
                            className={`checkboxBtn checked period-class-btn ${
                                config[ConfigProps.PERIOD_CLASSES][ConfigProps.HS_CLASS]
                            }`}
                        >
                            Hs
                        </div>
                    }
                    icon={null}
                    options={periodOptions}
                    onChange={(_, data) => {
                        onPeriodClassChange(ConfigProps.HS_CLASS, data.value);
                    }}
                />
                <Dropdown
                    value={config[ConfigProps.PERIOD_CLASSES][ConfigProps.QS_CLASS]}
                    trigger={
                        <div
                            className={`checkboxBtn checked period-class-btn ${
                                config[ConfigProps.PERIOD_CLASSES][ConfigProps.QS_CLASS]
                            }`}
                        >
                            Qs
                        </div>
                    }
                    icon={null}
                    options={periodOptions}
                    onChange={(_, data) => {
                        onPeriodClassChange(ConfigProps.QS_CLASS, data.value);
                    }}
                />
            </div>
        </div>
    );
}

function LinesSettings({ config, onPropChange }) {
    const checkbox = (prop, label) => (
        <CheckboxBtn
            className="flex-inline flex-align-center flex-justify-center"
            label={label}
            checked={config[prop]}
            onChange={(data) => onPropChange(prop, data)}
        />
    );
    return (
        <div className="config-module">
            <ModuleHeader text="Borders" />
            {checkbox(ConfigProps.VERTICALS, <Popup trigger={<BorderVertical />} content="Vertical Borders" />)}
            {checkbox(ConfigProps.HORIZONTALS, <Popup trigger={<BorderHorizontal />} content="Horizontal Borders" />)}
        </div>
    );
}

function StepInput({ value, onChange, min, max, step }) {
    const [internalValue, setInternalValue] = useState(value);
    const handleChange = (value) => {
        const correctedValue = Math.max(min, Math.min(value, max));
        onChange(correctedValue);
        setInternalValue(correctedValue);
    };
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Icon
                style={{ margin: 0 }}
                className="cursorPointer"
                size="small"
                name="minus"
                onClick={() => handleChange(value - step)}
            />
            <Input
                input={{
                    style: {
                        border: 'none',
                        background: 'var(--button-inactive-bg)',
                        boxShadow: 'var(--select-box-shadow)',
                        padding: '0',
                        height: '24px',
                        width: '24px',
                        borderRadius: 3,
                        textAlign: 'center',
                        outline: 'none',
                    },
                    min,
                    max,
                }}
                onChange={(e) => setInternalValue(e.target.value)}
                onBlur={() => handleChange(internalValue)}
                name="font-size"
                value={internalValue}
            />
            <Icon
                style={{ margin: 0 }}
                onClick={() => handleChange(value + step)}
                className="cursorPointer"
                size="small"
                name="plus"
            />
        </div>
    );
}

//TODO Rename
function TableAndP2pConfigV2({ tableConfig, onTableConfigChange, chartsRef }) {
    return (
        <NewClientsAdvancedSettings
            tableConfig={tableConfig}
            onTableConfigChange={onTableConfigChange}
            chartsRef={chartsRef}
        />
    );
}

function ModuleHeader({ text }) {
    return (
        <Header className="config-module__header" as="h5" textAlign="center">
            {text}
        </Header>
    );
}

export default TableAndP2pConfigV2;
